import React, { useEffect, useRef } from "react";
import * as vega from "vega";
import { expressionInterpreter } from "vega-interpreter";
import * as vegaLite from "vega-lite";

const VisualizationRenderers = ({ spec, data }) => {
  const containerRef = useRef(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !spec) return;

    // Create a copy of the spec and inject data if provided
    const specWithData = { ...spec };
    if (data && Array.isArray(data) && data.length > 0) {
      specWithData.data = { values: data };
    }

    // Clear previous view
    if (viewRef.current) {
      viewRef.current.finalize();
      viewRef.current = null;
    }
    containerRef.current.innerHTML = "";

    try {
      // Check if this is a Vega-Lite spec (has $schema or encoding property)
      const isVegaLite = specWithData.$schema?.includes('vega-lite') || 
                         (specWithData.encoding && !specWithData.scales);
      
      let vegaSpec;
      if (isVegaLite) {
        // Compile Vega-Lite spec to Vega
        vegaSpec = vegaLite.compile(specWithData).spec;
      } else {
        // It's already a Vega spec
        vegaSpec = specWithData;
      }
      
      // Parse the Vega spec with AST enabled (required for interpreter)
      // The interpreter needs AST to evaluate expressions without eval()
      const runtime = vega.parse(vegaSpec, null, { ast: true });
      const view = new vega.View(runtime, {
        renderer: 'canvas',
        container: containerRef.current,
        expr: expressionInterpreter,
        hover: true,
        tooltip: true
      });
      
      // Initialize and render the view
      view.runAsync()
        .then(() => {
          viewRef.current = view;
        })
        .catch((error) => {
          console.error("Error rendering chart:", error);
          if (containerRef.current) {
            containerRef.current.innerHTML = `
              <div style="
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: #6b7280;
                padding: 20px;
                text-align: center;
              ">
                <div style="font-size: 32px; margin-bottom: 12px;">⚠️</div>
                <div style="font-weight: 500; margin-bottom: 4px;">Unable to render chart</div>
                <div style="font-size: 12px; color: #9ca3af;">${error.message || "Unknown error"}</div>
              </div>
            `;
          }
        });
    } catch (error) {
      console.error("Error parsing chart spec:", error);
      if (containerRef.current) {
        containerRef.current.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #6b7280;
            padding: 20px;
            text-align: center;
          ">
            <div style="font-size: 32px; margin-bottom: 12px;">⚠️</div>
            <div style="font-weight: 500; margin-bottom: 4px;">Unable to render chart</div>
            <div style="font-size: 12px; color: #9ca3af;">${error.message || "Unknown error"}</div>
          </div>
        `;
      }
    }

    // Cleanup
    return () => {
      if (viewRef.current) {
        try {
          viewRef.current.finalize();
        } catch (e) {
          // Ignore cleanup errors
        }
        viewRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [spec, data]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  );
};

export default VisualizationRenderers;